import { useCallback, useMemo, useState } from "react";
import { useI18n } from "@core/i18n/i18n";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { TemplateItem } from "@domain/template/template";

export enum TemplateSortKey {
	LastEdited = "LastEdited",
	TemplateName = "TemplateName",
}

type SortOption = {
	label: string;
	key: TemplateSortKey;
};

type StrictSortOption<K extends TemplateSortKey> = {
	label: string;
	key: K;
};

type AllSortOptions = SortOption[];

export function useTemplateSort(programs: TemplateItem[]): {
	allSortOptions: AllSortOptions;
	setSortKey: (key: TemplateSortKey) => void;
	sortedTemplates: TemplateItem[];
} {
	const { format } = useI18n();
	const collator = useMemo(() => new Intl.Collator(), []);
	const { isDesktop } = useDimensions();
	const [sortKey, setSortKey] = useState(TemplateSortKey.LastEdited);

	const compareByLastEdited = useCallback(
		(a: TemplateItem, b: TemplateItem) => b.lastEditionDate.diff(a.lastEditionDate),
		[],
	);

	const compareByTemplateName = useCallback(
		(a: TemplateItem, b: TemplateItem) => {
			const result = collator.compare(a.name, b.name);
			if (result !== 0) return result;
			return compareByLastEdited(a, b);
		},
		[collator, compareByLastEdited],
	);

	const sortedTemplates = useMemo(() => {
		switch (sortKey) {
			case TemplateSortKey.LastEdited:
				return [...programs].sort((a, b) => compareByLastEdited(a, b));
			case TemplateSortKey.TemplateName:
				return [...programs].sort((a, b) => compareByTemplateName(a, b));
		}
	}, [sortKey, programs, compareByLastEdited, compareByTemplateName]);

	const allSortOptions = useMemo(
		() =>
			isDesktop
				? Object.values({
						[TemplateSortKey.LastEdited]: {
							label: format("templateListing.templates.sort.lastEdited"),
							key: TemplateSortKey.LastEdited,
						},
						[TemplateSortKey.TemplateName]: {
							label: format("templateListing.templates.sort.templateName"),
							key: TemplateSortKey.TemplateName,
						},
					} as { [K in TemplateSortKey]: StrictSortOption<K> })
				: [],
		[format, isDesktop],
	);

	return {
		allSortOptions,
		setSortKey,
		sortedTemplates,
	};
}
